import React, { useMemo } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import { IMultiParameters } from '../../../types/kpis';
import { sumArrays } from '../../../utils/helper';
import { palette } from '../palette';
import './styles.css';

interface IResultsChart {
  parameter: 'DF' | 'UF' | 'MTBF' | 'MTTR';
  data: IMultiParameters[];
  width?: number;
}

const ResultsChart: React.FC<IResultsChart> = ({
  parameter,
  data,
  width = 600,
}) => {
  const { t } = useTranslation();

  const chartData = useMemo(() => {
    const values: { name: string; data: number[]; type: string }[] = [];
    data.forEach(dt => {
      values.push({
        name: dt.name,
        data: dt.values,
        type: 'column',
      });
    });

    const monthsData = data.map(dt => dt.values);
    const sumMonthsData = sumArrays(...monthsData);
    const averageData = sumMonthsData.map(val => val / monthsData.length);

    values.push({
      name: 'Average',
      data: averageData,
      type: 'line',
    });

    return values;
  }, [data]);

  const getTitles = useMemo((): { y1: string; y2: string } => {
    if (parameter === 'DF') {
      return {
        y1: t('screens:results.impactsOnPhysicalAvailability').toUpperCase(),
        y2: `${t('screens:results.physicalAvailability').toUpperCase()} (%)`,
      };
    }
    if (parameter === 'UF') {
      return {
        y1: t('screens:results.impactsOnPhysicalUtilization').toUpperCase(),
        y2: `${t('screens:results.physicalUtilization').toUpperCase()} (%)`,
      };
    }
    if (parameter === 'MTBF') {
      return {
        y1: t('screens:results.impactsOnMTBF').toUpperCase(),
        y2: `MTBF (${t('components:utils.hours').toUpperCase()})`,
      };
    }
    if (parameter === 'MTTR') {
      return {
        y1: t('screens:results.impactsOnMTTR').toUpperCase(),
        y2: `MTTR (${t('components:utils.hours').toUpperCase()})`,
      };
    }
    return {
      y1: '',
      y2: '',
    };
  }, [parameter, t]);

  const getPalette = (numberOfColors: number, colors: string[]): string[] => {
    const newPalette = colors.slice(0, numberOfColors);
    newPalette.push('#333333');
    return newPalette;
  };

  const strokeArray = new Array(data.length).fill(0);

  const yAxisConfig = {
    show: false,
    max: 1,
    min: -1,
    tickAmount: 10,
    axisTicks: {
      show: true,
    },
    axisBorder: {
      show: true,
    },
    decimalsInFloat: 0,
    title: {
      text: getTitles.y1,
    },
    labels: {
      show: true,
      formatter: (value: any) => `${(value * 100).toFixed(0)}%`,
    },
  };

  const seriesYAxis = new Array(data.length - 1).fill(yAxisConfig);

  return (
    chartData && (
      <Chart
        type="bar"
        width={width}
        height={400}
        series={chartData}
        options={{
          chart: {
            fontFamily: 'Poppins',
            type: 'bar',
            stacked: true,
            stackType: 'normal',
            sparkline: {
              enabled: false,
            },
            toolbar: {
              show: false,
            },
            zoom: {
              enabled: false,
            },
          },
          plotOptions: {
            bar: {
              horizontal: false,
              columnWidth: '36px',
            },
          },
          stroke: {
            width: [...strokeArray, 4],
            colors: ['#333333'],
            curve: 'straight',
            lineCap: 'square',
          },
          colors: getPalette(data.length, palette),
          dataLabels: {
            enabled: false,
          },
          xaxis: {
            categories: [
              t('dates:months.jan').toUpperCase().substring(0, 3),
              t('dates:months.feb').toUpperCase().substring(0, 3),
              t('dates:months.mar').toUpperCase().substring(0, 3),
              t('dates:months.apr').toUpperCase().substring(0, 3),
              t('dates:months.may').toUpperCase().substring(0, 3),
              t('dates:months.jun').toUpperCase().substring(0, 3),
              t('dates:months.jul').toUpperCase().substring(0, 3),
              t('dates:months.aug').toUpperCase().substring(0, 3),
              t('dates:months.sep').toUpperCase().substring(0, 3),
              t('dates:months.oct').toUpperCase().substring(0, 3),
              t('dates:months.nov').toUpperCase().substring(0, 3),
              t('dates:months.dec').toUpperCase().substring(0, 3),
            ],
            position: 'bottom',
            axisTicks: {
              show: false,
            },
            axisBorder: {
              show: true,
            },
          },
          yaxis: [
            {
              max: 1,
              min: -1,
              tickAmount: 10,
              axisTicks: {
                show: true,
              },
              axisBorder: {
                show: true,
              },
              decimalsInFloat: 0,
              title: {
                text: getTitles.y1,
              },
              labels: {
                show: true,
                formatter: (value: any) => {
                  if (Math.trunc(value * 100) === 0) {
                    return `0%`;
                  }
                  return `${(value * 100).toFixed(0)}%`;
                },
              },
            },
            ...seriesYAxis,
            {
              max: 1,
              min: -1,
              // min: parameter === 'DF' || parameter === 'UF' ? 0.2 : 0.1,
              // max: parameter === 'DF' || parameter === 'UF' ? 1 : 0.9,
              tickAmount: 10,
              decimalsInFloat: 0,
              opposite: true,
              axisBorder: {
                show: true,
              },
              axisTicks: {
                show: true,
              },
              title: {
                text: getTitles.y2,
              },
              labels: {
                show: true,
                formatter: (value: any) => {
                  if (Math.trunc(value * 100) === 0) {
                    if (parameter === 'DF' || parameter === 'UF') {
                      return `0%`;
                    }
                    return `0h`;
                  }
                  if (parameter === 'DF' || parameter === 'UF') {
                    return `${(value * 100).toFixed(0)}%`;
                  }
                  if (parameter === 'MTBF' || parameter === 'MTTR') {
                    return `${(value * 100).toFixed(0)}h`;
                  }
                  return String(value);
                },
              },
            },
          ],
          annotations: {
            yaxis: [
              {
                y: 0,
                borderColor: '#C4C6C9',
                borderWidth: 2,
              },
            ],
          },
          fill: {
            opacity: 1,
          },
          tooltip: {
            enabled: true,
            custom({ series, seriesIndex, dataPointIndex, w }) {
              const formatValue = (val: any): string => {
                if (parameter === 'DF' || parameter === 'UF') {
                  return `${(val * 100).toFixed(2)}%`;
                }
                return `${(val * 100).toFixed(0)}h`;
              };

              const formattedValue = formatValue(
                series[seriesIndex][dataPointIndex],
              );
              const formattedAverage = formatValue(
                series[series.length - 1][dataPointIndex],
              );

              return `${'<div class="custom-tooltip">'}
                ${'<strong class="custom-tooltip-text">'} ${
                w.globals.seriesNames[seriesIndex]
              } ${'</strong>'}: ${'<span class="custom-tooltip-text">'} ${formattedValue} ${'</span>'} ${'<br/>'}
              ${'<strong class="custom-tooltip-text">'} ${t(
                'components:charts.averageLabel',
              )} ${'</strong>'}: ${'<span class="custom-tooltip-text">'} ${formattedAverage} ${'</span>'}
                ${'</div>'}`;
            },
          },
          grid: {
            strokeDashArray: 4,
            yaxis: {
              lines: {
                show: false,
              },
            },
            xaxis: {
              lines: {
                show: false,
              },
            },
          },
          legend: {
            show: false,
          },
        }}
      />
    )
  );
};

export default ResultsChart;
